import {
  faArrowLeft,
  faArrowRight,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  ProgressBar,
  Card,
} from "react-bootstrap";
import tipkitLogo from "../../../../images/tipkit-logo.png";
import { useDispatch, useSelector } from "react-redux";
import { submitOnboardingForm } from "../../../../redux/slices/onboardingFormSlice";
import { CARD_DESIGN_OPTIONS } from "../../../../constants";
import axios from "axios";
import { BACKEND_URL } from "../../../../config";
import { message } from "antd";

const ClientOnboardingForm = () => {
  const steps = ["Basic Details", "Services", "Card & Logos"];
  const [step, setStep] = useState(0);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { loading, success, error } = useSelector(
    (state) => state.onboardingForm
  );

  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    basicDetails: {
      startDate: "",
      hotelName: "",
      hotelAddress: "",
      numberOfRooms: "",
      pointOfContact: "",
      contactEmail: "",
    },
    services: [
      { department: "Housekeepers", enabled: false, employeeCount: "" },
      { department: "Valets", enabled: false, employeeCount: "" },
      { department: "Bell Desk", enabled: false, employeeCount: "" },
      {
        department: "other department",
        enabled: false,
        employeeCount: "",
        name: "",
      },
    ],
    cardDesign: {
      option: "none",
      promotionalAds: false,
      chatbotInterest: "no",
    },
    logo: null,
  });

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadLoading(true);

    try {
      const uploadData = new FormData();
      uploadData.append("logo", file);

      const { data } = await axios.post(
        `${BACKEND_URL}api/upload/logo`,
        uploadData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (data?.success && data?.fileUrl) {
        setFormData((prev) => ({
          ...prev,
          logo: data.fileUrl,
        }));
        message.success("Logo uploaded successfully");
      } else {
        message.error(data?.message || "Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      message.error("Error uploading logo");
    } finally {
      setUploadLoading(false);
      e.target.value = ""; // reset input
    }
  };

  const handleChange = (e, section, field, index = null) => {
    const { name, value, type, checked, files } = e.target;

    if (section === "basicDetails") {
      setFormData((prev) => ({
        ...prev,
        basicDetails: { ...prev.basicDetails, [name]: value },
      }));
    } else if (section === "services" && index !== null) {
      const updatedServices = [...formData.services];
      if (type === "checkbox") {
        updatedServices[index].enabled = checked;
      } else {
        updatedServices[index][name] = value;
      }
      setFormData((prev) => ({ ...prev, services: updatedServices }));
    } else if (section === "cardDesign") {
      if (type === "checkbox") {
        setFormData((prev) => ({
          ...prev,
          cardDesign: { ...prev.cardDesign, [name]: checked },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          cardDesign: { ...prev.cardDesign, [name]: value },
        }));
      }
    } else if (section === "logo") {
      setFormData((prev) => ({ ...prev, logo: files[0] }));
    }
  };

  // Validation per step
  const validateStep = () => {
    let newErrors = {};

    if (step === 0) {
      const { startDate, hotelName, pointOfContact, contactEmail } =
        formData.basicDetails;
      if (!startDate) newErrors.startDate = "Required";
      if (!hotelName) newErrors.hotelName = "Required";
      if (!pointOfContact) newErrors.pointOfContact = "Required";
      if (!contactEmail) newErrors.contactEmail = "Required";
    }

    if (step === 1) {
      const hasService = formData.services.some((svc) => svc.enabled);
      if (!hasService) newErrors.services = "At least one service required";
    }

    if (step === 2) {
      if (!formData.cardDesign.option) {
        newErrors.cardDesign = "Card design option is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) setStep((s) => s + 1);
  };
  const handleBack = () => setStep((s) => s - 1);

  const handleSubmit = () => {
    console.log("Final Form Data:", formData);
    dispatch(submitOnboardingForm(formData));
  };

  const renderStep = () => {
    switch (step) {
      case 0: // --- Basic Details ---
        return (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className={errors.startDate ? "text-danger" : ""}>
                What day would you like to start using tipkit at your location?
                *
              </Form.Label>
              <Form.Control
                type="date"
                name="startDate"
                value={formData.basicDetails.startDate}
                onChange={(e) => handleChange(e, "basicDetails")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className={errors.hotelName ? "text-danger" : ""}>
                What Hotel do you represent? *
              </Form.Label>
              <Form.Control
                type="text"
                name="hotelName"
                value={formData.basicDetails.hotelName}
                onChange={(e) => handleChange(e, "basicDetails")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>What is the address of your Hotel?</Form.Label>
              <Form.Control
                type="text"
                name="hotelAddress"
                value={formData.basicDetails.hotelAddress}
                onChange={(e) => handleChange(e, "basicDetails")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Number of rooms?</Form.Label>
              <Form.Control
                type="number"
                name="numberOfRooms"
                value={formData.basicDetails.numberOfRooms}
                onChange={(e) => handleChange(e, "basicDetails")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label
                className={errors.pointOfContact ? "text-danger" : ""}
              >
                Who will be the point of contact? *
              </Form.Label>
              <Form.Control
                type="text"
                name="pointOfContact"
                value={formData.basicDetails.pointOfContact}
                onChange={(e) => handleChange(e, "basicDetails")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className={errors.contactEmail ? "text-danger" : ""}>
                What is their email address? *
              </Form.Label>
              <Form.Control
                type="email"
                name="contactEmail"
                value={formData.basicDetails.contactEmail}
                onChange={(e) => handleChange(e, "basicDetails")}
              />
            </Form.Group>
          </Form>
        );

      case 1: // --- Services ---
        return (
          <Form>
            {formData.services.map((svc, idx) => (
              <div
                key={idx}
                className={`mb-3 border rounded p-3 ${
                  errors.services ? "border-danger" : ""
                }`}
              >
                <Form.Check
                  type="checkbox"
                  label={`Will you be using Tipkit for ${svc.department}?`}
                  checked={svc.enabled}
                  onChange={(e) => handleChange(e, "services", null, idx)}
                />
                {svc.enabled && (
                  <>
                    {svc.department === "other department" && (
                      <Form.Group className="mt-2">
                        <Form.Label>
                          Is there a department you would like to add, that has
                          not been mentioned? If yes, please list below:
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={svc.name}
                          onChange={(e) =>
                            handleChange(e, "services", null, idx)
                          }
                        />
                      </Form.Group>
                    )}
                    <Form.Group className="mt-2">
                      <Form.Label>
                        How many{" "}
                        {svc.department === "other department"
                          ? "persons"
                          : svc.department}{" "}
                        are typically employed at any given time?
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="employeeCount"
                        value={svc.employeeCount}
                        onChange={(e) => handleChange(e, "services", null, idx)}
                      />
                    </Form.Group>
                  </>
                )}
              </div>
            ))}
            {errors.services && (
              <small className="text-danger">{errors.services}</small>
            )}
          </Form>
        );

      case 2: // --- Card Designs & Logos ---
        return (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Please choose your card design option for this department:
              </Form.Label>
              <Form.Select
                name="option"
                value={formData.cardDesign.option}
                onChange={(e) => handleChange(e, "cardDesign")}
              >
                <option value="">Select Option</option>
                {Object.entries(CARD_DESIGN_OPTIONS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Form.Select>
              {errors.cardDesign && (
                <small className="text-danger">{errors.cardDesign}</small>
              )}
            </Form.Group>

            <Form.Group className="my-4">
              <Form.Check
                type="checkbox"
                name="promotionalAds"
                label="Would you like to explore promotional advertisements?"
                checked={formData.cardDesign.promotionalAds}
                onChange={(e) => handleChange(e, "cardDesign")}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Are you interested in building an advanced help chatbot for your
                hotel?
              </Form.Label>
              <Form.Select
                name="chatbotInterest"
                value={formData.cardDesign.chatbotInterest}
                onChange={(e) => handleChange(e, "cardDesign")}
              >
                <option value="yes">Yes</option>
                <option value="maybe">Maybe</option>
                <option value="no">No</option>
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label>Upload Your Logo</Form.Label>
              <Form.Control
                type="file"
                name="logo"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={uploadLoading}
              />
              {uploadLoading && (
                <small className="text-primary d-block mt-1">
                  Uploading...
                </small>
              )}

              {formData.logo && (
                <div className="mt-3 text-center">
                  <p className="fw-semibold">Preview</p>
                  <img
                    src={formData.logo}
                    alt="Uploaded Logo"
                    style={{
                      maxWidth: "150px",
                      maxHeight: "150px",
                      objectFit: "contain",
                    }}
                    className="border rounded p-2 bg-light"
                  />
                </div>
              )}
            </Form.Group>
          </Form>
        );

      default:
        return null;
    }
  };

  return (
    <Container
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: "100vh" }} // full viewport height
    >
      <Row className="justify-content-center w-100">
        <Col md={10} lg={8}>
          {loading ? (
            <div className="mb-3 d-flex justify-content-center">
              <div className="d-flex align-items-center text-primary">
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
                <span>Submitting your response...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          ) : success ? (
            <div className="alert alert-success text-center" role="alert">
              🎉 Form submitted successfully! We’ll get in touch soon.
            </div>
          ) : (
            <Card className="shadow-sm border-0 rounded-3">
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <img
                    src={tipkitLogo}
                    alt="Tipkit Logo"
                    style={{ maxWidth: "100px" }}
                    className="mb-0"
                  />
                  <span className="d-block fs-5 text-muted">
                    Client Onboarding
                  </span>
                </div>

                {/* Progress */}
                <ProgressBar
                  now={((step + 1) / steps.length) * 100}
                  label={`${steps[step]}`}
                  className="mb-4"
                />

                {/* Step Form */}
                {renderStep()}

                {/* Navigation */}
                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={handleBack}
                    disabled={step === 0}
                    className="px-4"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Previous
                  </Button>

                  {step === steps.length - 1 ? (
                    <Button
                      variant="success"
                      onClick={handleSubmit}
                      className="px-4"
                      disabled={loading || uploadLoading}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit{" "}
                          <FontAwesomeIcon icon={faCheck} className="ms-2" />
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={handleNext}
                      className="px-4"
                    >
                      Next{" "}
                      <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default ClientOnboardingForm;
